import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FiTv, FiWifi, FiGlobe, FiRadio, FiLayers, FiPlayCircle } from "react-icons/fi";
import Header from "../Header";
import { UserContext } from "../UserContext";
import Packages from "../Package";
import { broadbandPlans, railtelPlans, myPioneerPacks, myCombos } from "../../api/packagesApi";
import "./index.css";

const rupees = (n) => (n === null || n === undefined || n === "" ? "—" : `₹${Number(n).toLocaleString("en-IN")}`);
const errText = (err, fb) => err?.response?.data?.error || err?.response?.data?.message || err?.message || fb;

/** The package kinds on the Packages page. `adminTo`: the super admin's management page. */
const KINDS = [
  { key: "ott", title: "OTT Packages", icon: FiPlayCircle, text: "VRPlay, Watcho, PlayBox, ETV Win and other OTT plans." },
  { key: "internet", title: "Internet Packages", icon: FiWifi, text: "Pioneer broadband packages and sub plans.", flag: "internetEnabled" },
  { key: "dvpl", title: "DVPL Packages", icon: FiGlobe, text: "DVPL broadband packages and sub plans.", flag: "dvplEnabled" },
  { key: "railtel", title: "RailTel Packages", icon: FiRadio, text: "RailTel broadband plans.", flag: "railtelEnabled" },
  { key: "pioneer-iptv", title: "Pioneer IPTV", icon: FiTv, text: "DPO packs, A-la-carte channels and broadcaster packs.", adminTo: "/pioneer-iptv", flag: "iptv" },
  { key: "combos", title: "Combos", icon: FiLayers, text: "Internet + IPTV + OTT bundles.", adminTo: "/combo-plans", flag: "combos" },
];

// Pioneer IPTV counts as enabled for an admin / operator when it has DPO packs assigned
// (same rule as the customer page's Pioneer IPTV tab). Asked once per page load.
// Combos likewise only when at least one combo is assigned to the account.
let availCache = null; // { iptv, combos }
const useAvailable = (user) => {
  const isSuper = user?.role === "superadmin";
  const [avail, setAvail] = useState(isSuper ? { iptv: true, combos: true } : availCache);
  useEffect(() => {
    if (isSuper || availCache !== null) return undefined;
    let off = false;
    Promise.all([
      myPioneerPacks().then(({ data }) => !!(data?.success && (data.dpos || []).length)).catch(() => false),
      myCombos().then(({ data }) => !!(data?.success && (data.combos || []).length)).catch(() => false),
    ]).then(([iptv, combos]) => {
      availCache = { iptv, combos };
      if (!off) setAvail(availCache);
    });
    return () => { off = true; };
  }, [isSuper]);
  return avail; // null while checking
};
// true / false, or null while still checking
const flagOn = (k, user, avail) => {
  if (!k.flag) return true;
  if (k.flag === "iptv" || k.flag === "combos") return avail ? !!avail[k.flag] : null;
  return !!user?.[k.flag];
};

const visibleKinds = (user, avail) => {
  const isSuper = user?.role === "superadmin";
  return KINDS.filter((k) => isSuper || flagOn(k, user, avail) === true);
};

/** /packages — one place for every kind of package. */
export const PackagesHub = () => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const isSuper = user?.role === "superadmin";
  const avail = useAvailable(user);

  return (
    <>
      <Header />
      <div className="pkh-page">
        <div className="pkh-head">
          <h1>Packages</h1>
          <p>{isSuper ? "Manage every package you sell." : "The packages you can sell, at your price."}</p>
        </div>
        <div className="pkh-grid">
          {visibleKinds(user, avail).map((k) => {
            const Icon = k.icon;
            const to = isSuper && k.adminTo ? k.adminTo : `/packages/${k.key}`;
            return (
              <button key={k.key} type="button" className="pkh-tile" onClick={() => navigate(to)}>
                <span className="pkh-tile__icon"><Icon aria-hidden="true" /></span>
                <span className="pkh-tile__body">
                  <strong>{k.title}</strong>
                  <span>{k.text}</span>
                </span>
                <span className="pkh-tile__go" aria-hidden="true">→</span>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
};

/** A strip to jump between package kinds, shown above each list. */
const KindNav = ({ current }) => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const isSuper = user?.role === "superadmin";
  const avail = useAvailable(user);
  return (
    <nav className="pkh-nav" aria-label="Package kinds">
      <button type="button" className="pkh-nav__back" onClick={() => navigate("/packages")}>← All packages</button>
      <div className="pkh-nav__tabs">
        {visibleKinds(user, avail).map((k) => (
          <button key={k.key} type="button" className={k.key === current ? "is-on" : ""}
            onClick={() => navigate(isSuper && k.adminTo ? k.adminTo : `/packages/${k.key}`)}>
            {k.title}
          </button>
        ))}
      </div>
    </nav>
  );
};

const useLoad = (fn, deps) => {
  const [state, setState] = useState({ loading: true, error: "", data: null });
  useEffect(() => {
    let off = false;
    setState({ loading: true, error: "", data: null });
    fn()
      .then(({ data }) => {
        if (off) return;
        if (data && (data.status === "error" || data.success === false)) throw new Error(data.error || data.message || "Failed to load");
        setState({ loading: false, error: "", data });
      })
      .catch((err) => !off && setState({ loading: false, error: errText(err, "Failed to load"), data: null }));
    return () => { off = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return state;
};

const Search = ({ value, onChange, placeholder }) => (
  <input type="search" className="pkh-search" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
);
const matches = (q, text) => {
  const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const t = String(text || "").toLowerCase();
  return words.every((w) => t.includes(w));
};
const State = ({ s, empty }) =>
  s.loading ? <div className="pkh-empty">Loading…</div>
    : s.error ? <div className="pkh-empty pkh-empty--bad">{s.error}</div>
      : empty ? <div className="pkh-empty">{empty}</div> : null;

const BroadbandList = ({ type }) => {
  const s = useLoad(() => broadbandPlans(type), [type]);
  const [q, setQ] = useState("");
  const plans = useMemo(() => (s.data?.plans || []).filter((p) => (p.sub_plans || []).length), [s.data]);
  const shown = plans
    .map((p) => ({ ...p, sub_plans: matches(q, p.plan_name) ? p.sub_plans : p.sub_plans.filter((sp) => matches(q, `${p.plan_name} ${sp.sub_plan_name}`)) }))
    .filter((p) => p.sub_plans.length);
  return (
    <>
      <div className="pkh-tools"><Search value={q} onChange={setQ} placeholder="Search package or sub plan…" /></div>
      <State s={s} empty={!s.loading && !s.error && !shown.length ? (plans.length ? "Nothing matches." : "No packages are assigned to your account yet.") : null} />
      {!s.loading && !s.error && shown.length > 0 && (
        <div className="pkh-cards">
          {shown.map((p) => (
            <div key={p.id} className="pkh-card">
              <h3>{p.plan_name}</h3>
              {p.description ? <p className="pkh-muted">{p.description}</p> : null}
              <ul className="pkh-lines">
                {p.sub_plans.map((sp) => (
                  <li key={sp.id}><span>{sp.sub_plan_name}</span><b>{rupees(sp.base_price)}</b></li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

const RailtelList = () => {
  const s = useLoad(() => railtelPlans(), []);
  const [q, setQ] = useState("");
  const plans = (s.data?.plans || []).filter((p) => matches(q, `${p.plan_name} ${p.railtel_package_name}`));
  return (
    <>
      <div className="pkh-tools"><Search value={q} onChange={setQ} placeholder="Search RailTel plan…" /></div>
      <State s={s} empty={!s.loading && !s.error && !plans.length ? "No RailTel plans are assigned to your account yet." : null} />
      {!s.loading && !s.error && plans.length > 0 && (
        <div className="pkh-table-wrap">
          <table className="pkh-table">
            <thead><tr><th>Plan</th><th>RailTel package</th><th>Duration</th><th className="num">Price</th></tr></thead>
            <tbody>
              {plans.map((p) => (
                <tr key={p.id}>
                  <td><strong>{p.plan_name}</strong></td>
                  <td>{p.railtel_package_name}</td>
                  <td>{p.duration_months} {Number(p.duration_months) === 1 ? "month" : "months"}</td>
                  <td className="num">{rupees(p.base_price)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

const PioneerList = () => {
  const s = useLoad(() => myPioneerPacks(), []);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("DPO");
  const dpos = (s.data?.dpos || []).filter((p) => matches(q, p.name));
  const addons = (s.data?.addons || []).filter((p) => p.type === tab && matches(q, p.name));
  const rows = tab === "DPO" ? dpos : addons;
  return (
    <>
      <div className="pkh-tools">
        <div className="pkh-seg">
          {["DPO", "A-la-carte", "Broadcaster"].map((t) => (
            <button key={t} type="button" className={tab === t ? "is-on" : ""} onClick={() => setTab(t)}>{t}</button>
          ))}
        </div>
        <Search value={q} onChange={setQ} placeholder="Search pack…" />
      </div>
      <p className="pkh-muted">Prices are per 30 days and include FTA and 18% GST. Add-ons can be added to any DPO.</p>
      <State s={s} empty={!s.loading && !s.error && !rows.length ? (tab === "DPO" ? "No DPO packs are assigned to your account yet." : "Nothing to show.") : null} />
      {!s.loading && !s.error && rows.length > 0 && (
        <div className="pkh-table-wrap">
          <table className="pkh-table">
            <thead><tr><th>Pack</th><th className="num">Channels</th><th className="num">Price / 30 days</th></tr></thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id}>
                  <td><strong>{p.name}</strong>{tab === "DPO" ? <div className="pkh-tag">FTA included</div> : null}</td>
                  <td className="num">{p.channel_count ?? "—"}</td>
                  <td className="num">{rupees(p.price)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

const ComboList = () => {
  const s = useLoad(() => myCombos(), []);
  const combos = s.data?.combos || [];
  return (
    <>
      <State s={s} empty={!s.loading && !s.error && !combos.length ? "No combos are assigned to your account yet." : null} />
      {!s.loading && !s.error && combos.length > 0 && (
        <div className="pkh-cards">
          {combos.map((c) => (
            <div key={c.id} className="pkh-card">
              <div className="pkh-card__top">
                <h3>{c.name}</h3>
                <strong className="pkh-price">{rupees(c.price)}</strong>
              </div>
              {c.description ? <p className="pkh-muted">{c.description}</p> : null}
              <ul className="pkh-lines">
                {c.items.map((i, n) => (
                  <li key={n}>
                    <span>{i.label}</span>
                    <b className={i.is_free ? "pkh-free" : ""}>{i.is_free ? "FREE" : i.plan_name}</b>
                  </li>
                ))}
              </ul>
              {c.items.some((i) => i.provider === "pioneeriptv") && c.iptv_months > 1 ? (
                <p className="pkh-muted">IPTV {c.iptv_months} months · FTA included · add-ons optional</p>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </>
  );
};

/** /packages/:kind */
export const PackagesKind = () => {
  const { kind } = useParams();
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const isSuper = user?.role === "superadmin";
  const def = KINDS.find((k) => k.key === kind);
  const avail = useAvailable(user);

  useEffect(() => {
    if (!def) navigate("/packages", { replace: true });
    else if (isSuper && def.adminTo) navigate(def.adminTo, { replace: true });
    // a kind this account doesn't have (e.g. Pioneer IPTV without DPO packs): back to the list
    else if (!isSuper && flagOn(def, user, avail) === false) navigate("/packages", { replace: true });
  }, [def, isSuper, navigate, avail, user]);

  if (!def) return null;
  // OTT packages: the existing page (it has its own header)
  if (kind === "ott") return <Packages />;

  return (
    <>
      <Header />
      <div className="pkh-page">
        <KindNav current={kind} />
        <div className="pkh-head">
          <h1>{def.title}</h1>
          <p>{isSuper ? "Base prices." : "Your prices."}</p>
        </div>
        {(kind === "internet" || kind === "dvpl") && <BroadbandList type={kind} />}
        {kind === "railtel" && <RailtelList />}
        {kind === "pioneer-iptv" && <PioneerList />}
        {kind === "combos" && <ComboList />}
      </div>
    </>
  );
};

export default PackagesHub;
